import { CM_TO_IN } from './package-dimension-units';
import type { PackageUnitSystem } from './package-types.config';

/** Special handling codes shown as non-blocking Customer Packaging warnings */
export type CustomerSpecialHandlingType =
  | 'flatPackage'
  | 'oversized'
  | 'largePackage'
  | 'overMaximumLimit'
  | 'additionalHandling'
  | 'residentialHeavyWeight';

/**
 * OM-138 CR hierarchy (lower number = higher priority): SH07 > SH10 > SH09 > SH06 > SH05.
 * `overMaximumLimit` is outside this hierarchy and may co-exist as its own banner.
 */
export const CUSTOMER_SH_HIERARCHY_PRIORITY: Readonly<
  Record<Exclude<CustomerSpecialHandlingType, 'overMaximumLimit'>, number>
> = {
  largePackage: 1, // SH07
  residentialHeavyWeight: 2, // SH10
  oversized: 3, // SH09
  additionalHandling: 4, // SH06
  flatPackage: 5, // SH05
};

const LB_TO_KG = 0.45359237;

/**
 * Coerces measure values to a finite number for special-handling evaluation.
 */
function toFiniteNumber(value: unknown): number {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Converts a length in the active unit system to inches for special-handling / girth rules.
 * Metric uses ESN ×0.3937 (not ÷2.54). Callers ceil to whole inches for SH evaluation.
 */
export function lengthToInches(value: number, unitSystem: PackageUnitSystem): number {
  return unitSystem === 'metric' ? value * CM_TO_IN : value;
}

/**
 * Converts a weight in the active unit system to pounds for special-handling rules.
 */
export function weightToPounds(value: number, unitSystem: PackageUnitSystem): number {
  return unitSystem === 'metric' ? value / LB_TO_KG : value;
}

/**
 * Girth in inches: longest + 2×mid + 2×shortest (pass sides in any order; sorts internally).
 */
export function girthInches(lengthIn: number, widthIn: number, heightIn: number): number {
  const [longest, mid, shortest] = [lengthIn, widthIn, heightIn].sort((a, b) => b - a);
  return longest + 2 * mid + 2 * shortest;
}

export interface CustomerPieceMeasures {
  identicalPieces: unknown;
  perPieceWeight: unknown;
  length: unknown;
  width: unknown;
  height: unknown;
  requiresAdditionalHandling: boolean;
}

/** Optional shipment context for SH codes that depend on destination (e.g. SH10). */
export interface CustomerSpecialHandlingContext {
  destinationResidential?: boolean;
}

type HierarchySpecialHandlingType = Exclude<CustomerSpecialHandlingType, 'overMaximumLimit'>;

/**
 * Picks the highest-priority SH candidate for one piece (OM-138 hierarchy).
 */
export function selectCustomerSpecialHandlingWinner(
  candidates: readonly HierarchySpecialHandlingType[],
): HierarchySpecialHandlingType | null {
  if (candidates.length === 0) {
    return null;
  }

  return [...candidates].sort(
    (a, b) => CUSTOMER_SH_HIERARCHY_PRIORITY[a] - CUSTOMER_SH_HIERARCHY_PRIORITY[b],
  )[0];
}

/** Normalized piece measures in inches / pounds for SH evaluation. */
interface PieceMeasuresInInches {
  weightLb: number;
  lengthIn: number;
  widthIn: number;
  heightIn: number;
  longest: number;
  shortest: number;
  girth: number;
}

/**
 * Converts row measures to inches/lb for SH evaluation (PUROEE-9850 / OM-138).
 * Ceils each side to whole inches after conversion; does not mutate form values.
 * Returns null when any dimension is missing/non-positive.
 */
function resolvePieceMeasuresInInches(
  row: CustomerPieceMeasures,
  unitSystem: PackageUnitSystem,
): PieceMeasuresInInches | null {
  const weightLb = weightToPounds(toFiniteNumber(row.perPieceWeight), unitSystem);
  const lengthIn = Math.ceil(lengthToInches(toFiniteNumber(row.length), unitSystem));
  const widthIn = Math.ceil(lengthToInches(toFiniteNumber(row.width), unitSystem));
  const heightIn = Math.ceil(lengthToInches(toFiniteNumber(row.height), unitSystem));

  if (lengthIn <= 0 || widthIn <= 0 || heightIn <= 0) {
    return null;
  }

  const sides = [lengthIn, widthIn, heightIn].sort((a, b) => b - a);
  return {
    weightLb,
    lengthIn,
    widthIn,
    heightIn,
    longest: sides[0],
    shortest: sides[2],
    girth: girthInches(lengthIn, widthIn, heightIn),
  };
}

/**
 * Over Maximum Limit thresholds (outside SH hierarchy).
 */
function isOverMaximumSpecialHandling(measures: PieceMeasuresInInches): boolean {
  return measures.weightLb > 150 || measures.girth > 165 || measures.longest > 108;
}

/**
 * Collects hierarchy candidates for a fully measured piece (AH + Flat/Oversized/Large/SH10).
 */
function collectHierarchyCandidates(
  measures: PieceMeasuresInInches,
  context: CustomerSpecialHandlingContext,
  requiresAdditionalHandling: boolean,
): HierarchySpecialHandlingType[] {
  const candidates: HierarchySpecialHandlingType[] = [];

  if (requiresAdditionalHandling) {
    candidates.push('additionalHandling');
  }

  if (measures.longest > 18 && measures.longest <= 48 && measures.shortest <= 2) {
    candidates.push('flatPackage');
  }

  if (
    (measures.weightLb > 50 && measures.weightLb <= 150) ||
    (measures.longest > 48 && measures.longest <= 108)
  ) {
    candidates.push('oversized');
  }

  if (measures.girth > 130 && measures.girth <= 165) {
    candidates.push('largePackage');
  }

  // SH10: residential heavyweight (Package step has no HFPU yet → treat as not selected).
  if (
    context.destinationResidential === true &&
    measures.weightLb > 70 &&
    measures.weightLb <= 150
  ) {
    candidates.push('residentialHeavyWeight');
  }

  return candidates;
}

/**
 * Wraps a hierarchy winner in a single-element list (or empty).
 */
function listFromWinner(
  candidates: readonly HierarchySpecialHandlingType[],
): CustomerSpecialHandlingType[] {
  const winner = selectCustomerSpecialHandlingWinner(candidates);
  return winner ? [winner] : [];
}

/**
 * Over-max banner; AH may still apply (dimension-based SH codes do not).
 */
function overMaximumResult(requiresAdditionalHandling: boolean): CustomerSpecialHandlingType[] {
  const result: CustomerSpecialHandlingType[] = ['overMaximumLimit'];
  if (requiresAdditionalHandling) {
    result.push('additionalHandling');
  }
  return result;
}

/**
 * Evaluates non-blocking special-handling types for one package row.
 * Returns at most one hierarchy winner (SH07>SH10>SH09>SH06>SH05), plus
 * `overMaximumLimit` when applicable (outside the hierarchy).
 */
export function evaluateCustomerSpecialHandling(
  row: CustomerPieceMeasures,
  unitSystem: PackageUnitSystem,
  context: CustomerSpecialHandlingContext = {},
): CustomerSpecialHandlingType[] {
  const measures = resolvePieceMeasuresInInches(row, unitSystem);
  if (!measures) {
    return listFromWinner(row.requiresAdditionalHandling ? ['additionalHandling'] : []);
  }

  if (isOverMaximumSpecialHandling(measures)) {
    return overMaximumResult(row.requiresAdditionalHandling);
  }

  return listFromWinner(
    collectHierarchyCandidates(measures, context, row.requiresAdditionalHandling),
  );
}

/**
 * Unions per-piece hierarchy winners (and over-max) across all package rows.
 */
export function aggregateCustomerSpecialHandlingWarnings(
  rows: readonly CustomerPieceMeasures[],
  unitSystem: PackageUnitSystem,
  context: CustomerSpecialHandlingContext = {},
): CustomerSpecialHandlingType[] {
  const types = new Set<CustomerSpecialHandlingType>();
  for (const row of rows) {
    for (const type of evaluateCustomerSpecialHandling(row, unitSystem, context)) {
      types.add(type);
    }
  }

  const displayOrder: CustomerSpecialHandlingType[] = [
    'overMaximumLimit',
    'largePackage',
    'residentialHeavyWeight',
    'oversized',
    'additionalHandling',
    'flatPackage',
  ];

  return displayOrder.filter((type) => types.has(type));
}

/** Metric dual-unit threshold paired with 50 lb. */
const OVER_FIFTY_WARNING_KG = 22.68;

/**
 * Returns true when per-piece weight exceeds the 50 lb / 22.68 kg surcharge warning threshold.
 */
export function isOverFiftyPoundWarning(
  perPieceWeight: unknown,
  unitSystem: PackageUnitSystem,
): boolean {
  const weight = toFiniteNumber(perPieceWeight);
  if (unitSystem === 'metric') {
    return weight > OVER_FIFTY_WARNING_KG;
  }

  return weight > 50;
}
